import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { BehaviorSubject, filter, map, Observable, Subject, tap } from 'rxjs';
import { BooxApiService } from 'lib-boox';

/**
 * Dictionary interfac
 */
export interface IDictionary {
    /**
     * application name
     * @value string
     */
    application: string;
    /**
     * language code
     * @value string
     */
    language: string;
    /**
     * dictionary list
     * is a key value pair of translations
     * @value { [key: string]: string }
     */
    list: { [key: string]: string };
}

@Injectable({
    providedIn: 'root'
})
export class HeroDictionaryService {

    private _dictionary: IDictionary | null = null;

    /**
     * current loaded dictionary
     * @value IDictionary|null
     */
    public get dictionary() : IDictionary | null { return this._dictionary };
    /**
     * observable of the current loaded dictionary
     * @value Subject<IDictionary>
     */
    public onUpdate$: BehaviorSubject<IDictionary|null> = new BehaviorSubject<IDictionary|null>(null);

    constructor(
        private api: BooxApiService,
    ) {
        // update dictionary
        this.onUpdate$.subscribe((_dictionary: IDictionary|null) => this._dictionary = _dictionary);
    }

    /**
     * observe onKey change to dictionary updates for a specific key/pharse
     * 
     * @param filter_key key phrase to filter
     * 
     * @example
     * <HeroDictionaryService>.onKey$('key').subscribe(({key, value, found, language}) => {
     *      console.log(key, value, found, language);
     * });
     * 
     * @returns Observable<{key : string, value : string, found : boolean, language : string|null}>
     */
    public onKey$(filter_key: string): Observable<{ key: string, value: string, found: boolean, language: string | null }> {
        return this.onUpdate$.pipe(
            map((dictionary: IDictionary|null) => {
                // check if the dictionary is null
                if (!dictionary) {
                    return { key: filter_key, value: filter_key, found: false, language: null };
                }
                // check if the key is in the dictionary
                // if not, return the key as value, set found to false and set the language to null
                // if the key is in the dictionary return the key, value, found and language
                let pair = Object.entries(dictionary?.list || {})
                    .map(([key, value]) => ({ key, value, found: key == filter_key, language: dictionary.language }))
                    .filter(({ found }) => found);
                return pair.length ? pair[0] : { key: filter_key, value: filter_key, found: false, language: dictionary?.language || null };
            }),
        );
    }

    /**
     * Load a dictionary for a specific language and application
     * and update all subscribers by the onUpdate$ observable.
     * 
     * @param language code of the dictionary to load
     * @param application name load the dictionary for
     * 
     * @returns {Observable<IDictionary>}
     */
    public load$(language: string, application: string): Observable<IDictionary> {
        return this.api.get<IDictionary>(`/dictionary/${application}`, { params: { language } }).pipe(
            tap((_dictionary: IDictionary) => this.onUpdate$.next(_dictionary))
        );
    }

}
